import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { matchSorter } from 'match-sorter';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Link from '../../../shared/_TBComponents/Link';
import Text from '../../../shared/_TBComponents/Text';
import hexToRgba from '../../../shared/utils/hexToRgba';
import NavigatorPreviewPanel from './presentational/NavigatorPreviewPanel';
import NavigatorSelectorPanel from './presentational/NavigatorSelectorPanel';
import { fetchProgramPreview } from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  height: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-right: none;
  padding: 16px;
  gap: 8px;
  overflow-y: auto;
  box-shadow: inset 8px 0 12px -12px rgba(0,0,0,0.18);
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 700px;
  margin-bottom: ${(props) => (props.isMobile ? '12px' : '0')};
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

const ProgramItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 8px;
  gap: 8px;
  border: ${(props) => (props.isSelected
    ? `2px solid ${props.bgColor}`
    : '1px solid #EEE')};
  background: ${(props) => (props.isSelected ? props.selectedBg : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  height: 100%;
  max-width: 325px;
`;

const MobilePage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  padding: 12px 16px 4px;
  flex-shrink: 0;
`;

const MobileBackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileSectionTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #444;
  padding: 6px 0 4px;
`;

const MobileDetailContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px;
  min-height: 0;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #999; }
  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const ArrowLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #8EB8FF;
  }
`;

const ProgramNavigator = ({
  accountCode, programId, manageUsers, isMobile,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountPrimaryColor = currentUser?.id
    ? (theme?.colorPrimary || currentUser?.userSkin?.primary || '#FF6600')
    : undefined;
  const selectedProgramBg = accountPrimaryColor ? hexToRgba(accountPrimaryColor, 0.05) : 'transparent';

  const programs = useSelector((state) => state.navigator.data.programs);
  const programsError = useSelector((state) => state.navigator.data.programsError);
  const isLoading = useSelector((state) => state.navigator.ui.isLoadingPrograms);

  const [selectedProgram, setSelectedProgram] = useState(null);

  const isInvalidProgramId = !!programId
    && !isLoading
    && programs?.length > 0
    && !programs.some((p) => String(p.id) === String(programId));

  // Find program by programId from URL
  useEffect(() => {
    if (programId && programs.length > 0) {
      const found = programs.find((p) => String(p.id) === String(programId));
      setSelectedProgram(found || null);
    } else if (!programId) {
      setSelectedProgram(null);
    }
  }, [programId, programs]);

  useEffect(() => {
    if (selectedProgram && accountCode) {
      dispatch(fetchProgramPreview(accountCode, selectedProgram.id));
    }
  }, [selectedProgram?.id]);

  const selectorButtons = [
    ...(manageUsers ? [
      {
        name: 'Add',
        icon: 'add-program',
        iconSize: 20,
        iconColor: '#9E9E9E',
        href: '/program/create',
        onClick: (e) => {
          const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          if (!isModified) {
            e.preventDefault();
            history.push('/program/create');
          }
        },
      },
      {
        name: 'Manage',
        href: `${process.env.PHP_APP_URL || 'https://app.teambuildr.com'}/exercises?action=savedPrograms`,
        onClick: (e) => {
          const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          if (!isModified) {
            e.preventDefault();
            window.location.href = `${process.env.PHP_APP_URL || 'https://app.teambuildr.com'}/exercises?action=savedPrograms`;
          }
        },
      },
    ] : []),
  ];

  const renderProgramsInfo = ({ showTitle = true } = {}) => (
    <>
      {showTitle && (
      <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
        Programs
      </Text>
      )}
      <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
        Programs have relative days (Day 1, Day 2) instead of real dates (April 10, April 11).
      </Text>
      <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
        You can assign them as self-paced workouts or load them into calendars.
      </Text>
      <ArrowLink
        href='https://support.teambuildr.com/article/xyf01bviza-free-form-programming-overview'
        target='_blank'
        fontSize='18px'
        color='#77A7FF'
        fontWeight={700}
      >
        More about programs
        <Icon icon='alt-right-arrow' size={10} color='currentColor' />
      </ArrowLink>
    </>
  );

  const renderProgramList = ({ selectedRef, searchFilter }) => {
    const filtered = searchFilter && programs
      ? matchSorter(programs, searchFilter, { keys: ['programName'] })
      : programs;

    if (programsError && !searchFilter) {
      return (
        <Text styledTextProps={{ fontSize: '14px', color: '#666' }}>
          {programsError}
        </Text>
      );
    }

    if (!filtered || filtered.length === 0) {
      return (
        <Text styledTextProps={{ fontSize: '14px', color: '#666' }}>
          {searchFilter ? 'No programs match your search' : 'No programs found'}
        </Text>
      );
    }

    return filtered.map((program) => (
      <ProgramItem
        key={program.id}
        isSelected={selectedProgram?.id === program.id}
        bgColor={accountPrimaryColor}
        selectedBg={selectedProgramBg}
        ref={selectedProgram?.id === program.id ? selectedRef : null}
        onClick={() => history.push(`/dashboard/programs/${program.id}`)}
      >
        <Text
          styledTextProps={{
            fontSize: '16px',
            fontWeight: selectedProgram?.id === program.id ? 700 : 400,
          }}
        >
          {program.programName}
        </Text>
      </ProgramItem>
    ));
  };

  let mainContent;
  if (selectedProgram) {
    mainContent = (
      <>
        <HeaderContainer isMobile={isMobile}>
          <InfoWrapper>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
              {selectedProgram.programName}
            </Text>
            {manageUsers && (
              <Button
                href={`${process.env.PHP_APP_URL
                || 'https://app.teambuildr.com'}/exercises?action=savedPrograms&editProgramId=${selectedProgram.id}`}
                styledWrapperProps={{ paddingLeft: '8px', marginTop: '4px' }}
                name='Settings'
                fontSize='16px'
                styledTextProps={{ fontWeight: 700, letterSpacing: '0.5px' }}
                txtColor='#77A7FF'
                bgColor='transparent'
                noBorder
              />
            )}
          </InfoWrapper>
          <ButtonWrapper>
            <Button
              name='Edit Program'
              icon='edit-workout'
              iconColor='#fff'
              customBorderRadius='4px'
              noBorder
              bgColor='#444'
              txtColor='#fff'
              fontSize='16px'
              href={`/build?programId=${selectedProgram.id}`}
              styledWrapperProps={{
                boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
                padding: '6px 16px',
              }}
              styledTextProps={{ fontWeight: '700', letterSpacing: '0.5px' }}
            />
          </ButtonWrapper>
        </HeaderContainer>

        <NavigatorPreviewPanel
          type='program'
          label
          maxWidth='700px'
          previewCopy={renderProgramsInfo({ showTitle: false })}
        />
      </>
    );
  } else if (programsError && !isLoading) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {programsError}
        </Text>
      </EmptyState>
    );
  } else if (isInvalidProgramId) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
          Program could not be found
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          The program ID in the URL does not exist in this account.
        </Text>
      </EmptyState>
    );
  } else {
    mainContent = (
      <EmptyState>
        {renderProgramsInfo()}
      </EmptyState>
    );
  }

  if (isMobile) {
    if (programId) {
      return (
        <MobilePage>
          <MobileHeader>
            <MobileBackLink
              type='button'
              onClick={() => history.push('/dashboard/programs')}
            >
              <Icon icon='left-arrow' size={12} color='#77A7FF' />
              Back to Programs
            </MobileBackLink>
          </MobileHeader>
          <MobileDetailContainer>
            {mainContent}
          </MobileDetailContainer>
        </MobilePage>
      );
    }
    return (
      <MobilePage>
        <MobileHeader>
          <MobileBackLink
            type='button'
            onClick={() => history.push('/dashboard')}
          >
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            Back to Home
          </MobileBackLink>
          <MobileSectionTitle>Programs</MobileSectionTitle>
        </MobileHeader>
        <NavigatorSelectorPanel
          fullWidth
          buttons={selectorButtons}
          searchPlaceholder='Search programs'
          isLoading={isLoading}
          selectedId={selectedProgram?.id}
          restoreSelectedScrollRef={false}
          renderList={renderProgramList}
        />
      </MobilePage>
    );
  }

  return (
    <Container>
      <NavigatorSelectorPanel
        width={285}
        buttons={selectorButtons}
        searchPlaceholder='Search programs'
        isLoading={isLoading}
        selectedId={selectedProgram?.id}
        restoreSelectedScrollRef={Boolean(programId)}
        renderList={renderProgramList}
      />
      <Section>
        {mainContent}
      </Section>
    </Container>
  );
};

ProgramNavigator.propTypes = {
  accountCode: PropTypes.number,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  manageUsers: PropTypes.bool,
  isMobile: PropTypes.bool,
};

ProgramNavigator.defaultProps = {
  accountCode: undefined,
  programId: undefined,
  manageUsers: false,
  isMobile: false,
};

export default ProgramNavigator;
